@layer components {
  .frame {
    --color-focus: var(--color-accent);

    container-type: inline-size;
    border-radius: var(--radius-sheet);
    background: var(--color-surface);
    box-shadow: var(--shadow-sheet);
  }

  .table,
  .table [role="rowgroup"],
  .table [role="row"] {
    display: grid;
  }

  .table {
    --table-columns: minmax(0, 1fr) 7rem 7rem 8.5rem 8.5rem 6.5rem;
    --row-columns: repeat(4, minmax(0, 1fr)) auto;

    inline-size: 100%;
    font-variant-numeric: tabular-nums;
  }

  .headerLabel,
  .sortButton {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    min-block-size: var(--tap-target);
    padding-inline: var(--space-2);
  }

  .sortButton {
    border: 0;
    border-radius: var(--radius-full);
    background: none;
    color: inherit;
    font: inherit;
    font-size: var(--text-xs);
    font-weight: 620;
    white-space: nowrap;
    transition:
      background-color var(--duration-fast) var(--ease-out),
      color var(--duration-fast) var(--ease-out);

    @media (hover: hover) {
      &:hover {
        background: var(--color-surface-hover);
        color: var(--color-text);
      }
    }

    &[data-sorted] {
      color: var(--color-accent);
    }
  }

  .sortArrow {
    display: inline-grid;
    place-items: center;
    rotate: 90deg;
    opacity: 0.4;
    transition:
      rotate var(--duration-spring) var(--ease-spring),
      opacity var(--duration-fast) var(--ease-out);

    & svg {
      inline-size: 0.875rem;
      block-size: 0.875rem;
      stroke-width: 2.6;
    }

    &[data-direction] {
      opacity: 1;
    }

    &[data-direction="asc"] {
      rotate: -90deg;
    }
  }

  .name {
    font-weight: 620;
    line-height: 1.2;
    text-decoration: none;
    overflow-wrap: anywhere;

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: var(--radius-lg);
    }

    &:focus-visible {
      outline: none;

      &::after {
        outline: 2.5px solid var(--color-accent);
        outline-offset: -2.5px;
      }
    }
  }

  .typeInline,
  .subtle {
    color: var(--color-text-muted);
  }

  .typeInline {
    font-size: var(--text-sm);
    line-height: 1.35;
  }

  .location {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 560;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .open {
    display: grid;
    place-items: center;
    inline-size: var(--tap-target);
    block-size: var(--tap-target);
    color: var(--color-text-faint);

    & svg {
      inline-size: 1.125rem;
      block-size: 1.125rem;
      stroke-width: 2.2;
    }
  }

  .row[data-dragging] {
    opacity: 0.4;
  }

  .row[data-moving] {
    opacity: 0.55;
    pointer-events: none;
  }

  .row[data-pending] {
    cursor: default;

    & .name,
    & .typeInline {
      color: var(--color-text-muted);
    }

    & [data-kind] {
      animation: folder-wait 1.4s var(--ease-out) infinite;
    }
  }

  @keyframes folder-wait {
    50% {
      opacity: 0.55;
    }
  }

  .viewers {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.2rem 0.6rem 0.2rem 0.45rem;
    border-radius: var(--radius-full);
    background: var(--color-surface-sunken);
    color: var(--color-text-muted);
    font-weight: 560;
    white-space: nowrap;

    & svg {
      flex: none;
      inline-size: 0.95rem;
      block-size: 0.95rem;
      stroke-width: 2.2;
    }

    [data-viewers] > & {
      background: var(--color-accent-soft);
      color: var(--color-accent);
    }

    [data-viewers="public"] > & {
      background: var(--color-accent);
      color: var(--color-on-accent);
    }
  }

  .dropNote {
    justify-self: start;
    padding: 0.1rem 0.6rem;
    border-radius: var(--radius-full);
    background: var(--color-action);
    color: var(--color-on-action);
    font-size: var(--text-sm);
    font-weight: 650;
    animation: note-in var(--duration-spring) var(--ease-spring);
  }

  @keyframes note-in {
    from {
      scale: 0.7;
      opacity: 0;
    }
  }

  .actions {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
    gap: var(--space-1);
  }

  .nameInner {
    display: grid;
    grid-template-columns: 2.625rem minmax(0, 1fr);
    align-items: center;
    column-gap: var(--space-3);
  }

  .nameText {
    display: grid;
    gap: 0.125rem;
    min-inline-size: 0;
  }

  .row {
    position: relative;
    border-radius: var(--radius-lg);
    transition: background-color var(--duration-fast) var(--ease-out);

    & + .row::before {
      content: "";
      position: absolute;
      inset-inline: var(--space-4);
      inset-block-start: 0;
      border-block-start: 1.5px solid var(--color-border);
    }

    &[data-dropping]::before,
    &[data-dropping] + .row::before {
      border-color: transparent;
    }

    @media (hover: hover) {
      &:hover {
        background: var(--color-surface-hover);
      }

      &:hover::before,
      &:hover + .row::before {
        border-color: transparent;
      }

      &:hover [data-kind] {
        rotate: 0deg;
        translate: 0 -2px;
        scale: 1.1;
      }
    }

    &:focus-within [data-kind] {
      rotate: 0deg;
      scale: 1.1;
    }

    &[data-dropping] {
      background: color-mix(in oklab, var(--color-action), var(--color-surface) 55%);
    }

    &[data-dropping] [data-kind] {
      rotate: 0deg;
      translate: 0 -3px;
      scale: 1.15;
    }

    &[data-landed] {
      animation:
        land var(--duration-spring) var(--ease-spring),
        landed-glow 2.6s var(--ease-out) forwards;

      & [data-kind] {
        animation: sticker-land 720ms var(--ease-spring);
      }

      @media (prefers-reduced-motion: reduce) {
        background-color: var(--color-accent-soft);
        animation: none;

        & [data-kind] {
          animation: none;
        }
      }
    }
  }

  @keyframes land {
    from {
      opacity: 0;
      translate: 0 -0.75rem;
      scale: 0.97;
    }
  }

  @keyframes landed-glow {
    0%,
    45% {
      background-color: var(--color-accent-soft);
    }
    100% {
      background-color: transparent;
    }
  }

  @keyframes sticker-land {
    from {
      rotate: 0deg;
      translate: 0 -0.5rem;
      scale: 1.3;
    }
  }

  .fact {
    display: grid;
    gap: 0.125rem;
    font-size: var(--text-sm);
  }

  .label {
    color: var(--color-text-muted);
    font-size: var(--text-xs);
    font-weight: 560;
  }

  @container (inline-size >= 56rem) {
    .table {
      grid-template-columns: var(--table-columns);
      padding: var(--space-3);

      & [role="rowgroup"],
      & [role="row"] {
        grid-column: 1 / -1;
        grid-template-columns: subgrid;
      }

      & [role="columnheader"] {
        padding: 0 var(--space-1) var(--space-1);
        color: var(--color-text-muted);
        font-size: var(--text-xs);
        font-weight: 620;
        text-align: start;
        white-space: nowrap;

        &[data-col="name"] {
          padding-inline-start: calc(var(--space-4) + 2.625rem + var(--space-3) - var(--space-2));
        }
      }

      & [role="cell"] {
        padding: var(--space-2) var(--space-3);
        font-size: var(--text-sm);
      }

      & [data-align="end"] {
        text-align: end;
      }

      & [role="cell"][data-col="items"] {
        text-align: end;
      }

      & .row[data-kind="folder"] > [data-col="viewers"] {
        grid-column: 4;
      }
    }

    .table .nameCell {
      padding-inline-start: var(--space-4);
    }

    .nameInner {
      min-block-size: 2.875rem;
    }

    .name {
      font-size: var(--text-base);
    }

    .label {
      display: none;
    }

    .fact {
      align-content: center;
      white-space: nowrap;
    }

    .table .actions {
      align-items: center;
      padding-inline: 0 var(--space-2);
    }
  }

  @container (inline-size < 56rem) {
    .head {
      position: absolute;
      inline-size: 1px;
      block-size: 1px;
      overflow: hidden;
      clip-path: inset(50%);
    }

    .body {
      padding: var(--space-2);
    }

    .row {
      grid-template-columns: var(--row-columns);
    }

    .nameCell {
      grid-column: 1 / -2;
      grid-row: 1;
      min-inline-size: 0;
      padding: var(--space-4) 0 var(--space-2) var(--space-3);
    }

    .nameInner {
      min-block-size: 2.625rem;
    }

    .actions {
      grid-column: -2 / -1;
      grid-row: 1;
      align-self: start;
      padding: var(--space-3) var(--space-2) 0 0;
    }

    .fact {
      grid-row: 2;
      align-content: start;
      padding: var(--space-2) var(--space-2) var(--space-4) var(--space-3);
    }

    .fact[data-col="updated"] {
      grid-column: 4 / 6;
    }

    .row[data-kind="folder"] .fact[data-col="items"] {
      grid-column: 1 / 3;
    }

    .row[data-kind="folder"] .fact[data-col="viewers"] {
      grid-column: 3;
    }

    .fact > :last-child {
      justify-self: start;
      white-space: nowrap;
    }
  }

  @container (inline-size < 26rem) {
    .row {
      --row-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    }

    .fact {
      grid-row: auto;
      padding-block: var(--space-1);
    }

    .fact[data-col="size"],
    .fact[data-col="viewers"] {
      grid-column: 1;
    }

    .fact[data-col="version"],
    .fact[data-col="updated"] {
      grid-column: 2 / 4;
    }

    .fact[data-col="size"],
    .fact[data-col="version"] {
      padding-block-start: var(--space-2);
    }

    .fact[data-col="viewers"],
    .fact[data-col="updated"] {
      padding-block-end: var(--space-4);
    }

    .row[data-kind="folder"] .fact[data-col="items"] {
      grid-column: 1;
      padding-block-start: var(--space-2);
    }

    .row[data-kind="folder"] .fact[data-col="viewers"] {
      grid-column: 2 / 4;
      padding-block: var(--space-2) var(--space-1);
    }

    .row[data-kind="folder"] .fact[data-col="updated"] {
      grid-column: 1 / 4;
      padding-block-end: var(--space-4);
    }
  }
}
